/**
 * App
 * Root component with routing and protected route wrapper.
 */
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProviderLayout from './components/ProviderLayout';
import ProviderDashboard from './pages/ProviderDashboard';
import LeadManagement from './pages/LeadManagement';
import SubscriptionManagement from './pages/SubscriptionManagement';
import FeaturedListingManagement from './pages/FeaturedListingManagement';
import ProviderProfile from './pages/ProviderProfile';
import ProviderAnalytics from './pages/ProviderAnalytics';
import ProviderReviews from './pages/ProviderReviews';
import APIAccessPage from './pages/APIAccessPage';
import OnboardingWizard from './pages/Onboarding/OnboardingWizard';

// Lazy-loaded batch-6 pages
const ReviewManagement = React.lazy(() => import('./pages/ReviewManagement'));
const CompetitorBenchmark = React.lazy(() => import('./pages/CompetitorBenchmark'));
const LeadSettings = React.lazy(() => import('./pages/LeadSettings'));
const ProviderTranslations = React.lazy(() => import('./pages/ProviderTranslations'));
const ProviderEngagement = React.lazy(() => import('./pages/ProviderEngagement'));
const ProviderLocationManager = React.lazy(() => import('./pages/ProviderLocationManager'));

// Lazy-loaded Batch 15 pages
const MediaLibrary = React.lazy(() => import('./pages/MediaLibrary'));
const ProviderSetupWizard = React.lazy(() => import('./pages/ProviderSetupWizard'));
const ProviderFavoriteStats = React.lazy(() => import('./pages/ProviderFavoriteStats'));
const ProviderQADashboard = React.lazy(() => import('./pages/ProviderQADashboard'));
const ProviderAPIKeys = React.lazy(() => import('./pages/ProviderAPIKeys'));

// Lazy-loaded Batch 16 pages
const ProviderPromotions = React.lazy(() => import('./pages/ProviderPromotions'));
const ProviderEmbedWidgets = React.lazy(() => import('./pages/ProviderEmbedWidgets'));
const ProviderBilling = React.lazy(() => import('./pages/ProviderBilling'));
const ProviderDeepLinks = React.lazy(() => import('./pages/ProviderDeepLinks'));

// Lazy-loaded Batch 17 pages
const ProviderAlertInsights = React.lazy(() => import('./pages/ProviderAlertInsights'));
const ProviderComplianceReports = React.lazy(() => import('./pages/ProviderComplianceReports'));
const ProviderPortfolioManager = React.lazy(() => import('./pages/ProviderPortfolioManager'));

// Lazy-loaded Batch 19 pages
const ProviderTrustSeals = React.lazy(() => import('./pages/ProviderTrustSeals'));

// Lazy-loaded Batch 20 pages
const ProviderRecommendationInsights = React.lazy(() => import('./pages/ProviderRecommendationInsights'));

// Lazy-loaded Batch 21 pages
const ProviderFeedbackInsights = React.lazy(() => import('./pages/ProviderFeedbackInsights'));

// Lazy-loaded Batch 22 pages
const ProviderAffiliateStats = React.lazy(() => import('./pages/ProviderAffiliateStats'));

// Lazy-loaded Batch 23 pages
const ProviderSubscriptionPage = React.lazy(() => import('./pages/ProviderSubscriptionPage'));

// Lazy-loaded Batch 24 pages (Provider Directory Listing Editor)
const ProviderListingEditor = React.lazy(() => import('./pages/ProviderListingEditor'));

// Lazy-loaded Batch 26 pages (Provider Badges, Pricing Benchmark)
const ProviderBadgesPage = React.lazy(() => import('./pages/ProviderBadgesPage'));
const ProviderPricingBenchmark = React.lazy(() => import('./pages/ProviderPricingBenchmark'));

// Lazy-loaded Batch 27 pages
const MyServiceAreas = React.lazy(() => import('./pages/MyServiceAreas'));
const MyWaitTimes = React.lazy(() => import('./pages/MyWaitTimes'));
const MyEvents = React.lazy(() => import('./pages/MyEvents'));
const MyReviewIncentives = React.lazy(() => import('./pages/MyReviewIncentives'));
const ProviderCommunications = React.lazy(() => import('./pages/ProviderCommunications'));

// Lazy-loaded Batch 28 pages
const ResponseTemplates = React.lazy(() => import('./pages/ResponseTemplates'));
const MyEndorsements = React.lazy(() => import('./pages/MyEndorsements'));
const ManageHours = React.lazy(() => import('./pages/ManageHours'));

// Lazy-loaded Batch 29 pages
const ManageStaff = React.lazy(() => import('./pages/ManageStaff'));
const ManageFAQ = React.lazy(() => import('./pages/ManageFAQ'));
const MyPriceHistory = React.lazy(() => import('./pages/MyPriceHistory'));
const ProviderComplaints = React.lazy(() => import('./pages/ProviderComplaints'));
const DisputeCenter = React.lazy(() => import('./pages/DisputeCenter'));
const AnalyticsOverview = React.lazy(() => import('./pages/AnalyticsOverview'));
const TrafficAnalytics = React.lazy(() => import('./pages/TrafficAnalytics'));
const MarketInsights = React.lazy(() => import('./pages/MarketInsights'));
const PerformanceBenchmark = React.lazy(() => import('./pages/PerformanceBenchmark'));
const ContactRequestsPage = React.lazy(() => import('./pages/ContactRequestsPage'));
const ProviderSupportCenter = React.lazy(() => import('./pages/ProviderSupportCenter'));

// Lazy-loaded Batch 30 pages
const ManageCredentials = React.lazy(() => import('./pages/ManageCredentials'));
const ManagePackages = React.lazy(() => import('./pages/ManagePackages'));
const ManageInsurance = React.lazy(() => import('./pages/ManageInsurance'));
const ProviderReminders = React.lazy(() => import('./pages/ProviderReminders'));

// Lazy-loaded Batch 31 pages
const ManagePortfolio = React.lazy(() => import('./pages/ManagePortfolio'));
const MyIntegrations = React.lazy(() => import('./pages/MyIntegrations'));
const ProviderBulkTools = React.lazy(() => import('./pages/ProviderBulkTools'));
const ProviderExports = React.lazy(() => import('./pages/ProviderExports'));
const ManageAvailability = React.lazy(() => import('./pages/ManageAvailability'));

// Lazy-loaded Batch 32 pages
const MyCompliance = React.lazy(() => import('./pages/MyCompliance'));
const CompetitorInsights = React.lazy(() => import('./pages/CompetitorInsights'));
const ProviderSettings = React.lazy(() => import('./pages/ProviderSettings'));
const ProviderContent = React.lazy(() => import('./pages/ProviderContent'));
const ProviderAuditLog = React.lazy(() => import('./pages/ProviderAuditLog'));

// Lazy-loaded Batch 33 pages
const LiveNotifications = React.lazy(() => import('./pages/LiveNotifications'));
const ProviderDataConsent = React.lazy(() => import('./pages/ProviderDataConsent'));
const ProviderSessions = React.lazy(() => import('./pages/ProviderSessions'));

// Lazy-loaded Batch 34 pages
const ProviderApiVersions = React.lazy(() => import('./pages/ProviderApiVersions'));
const ProviderApiPerformance = React.lazy(() => import('./pages/ProviderApiPerformance'));
const ProviderAssets = React.lazy(() => import('./pages/ProviderAssets'));
const ProviderServiceHealth = React.lazy(() => import('./pages/ProviderServiceHealth'));
const ProviderDemoData = React.lazy(() => import('./pages/ProviderDemoData'));

// Lazy-loaded earnings page

// Lazy-loaded Merchant Dashboard pages
const MerchantAvailability = React.lazy(() => import('./pages/AvailabilityManager'));
const MerchantPromotions = React.lazy(() => import('./pages/PromotionsManager'));
const MerchantSettings = React.lazy(() => import('./pages/ProviderSettings'));
const CommissionsPage = React.lazy(() => import('./pages/CommissionsPage'));

/**
 * ProtectedRoute
 * Redirects to /login if no provider_token in localStorage.
 */
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('provider_token');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

/**
 * Simple login page placeholder.
 */
function LoginPage() {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // One sign-in for the whole platform. /api/provider-portal/auth/login
      // never existed (found 2026-09-17); the portal could not log anyone in.
      // The access token carries providerId when the account manages a
      // verified provider; without one there is nothing for the portal to show.
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || json.error || 'Login failed');
      if (!json.user?.providerId) {
        throw new Error('This account is not linked to a provider. Claim your business on the main site first, then sign in here.');
      }
      localStorage.setItem('provider_token', json.accessToken);
      localStorage.setItem('provider_id', String(json.user.providerId));
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '40px',
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px', textAlign: 'center' }}>
          Provider Dashboard
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', marginBottom: '32px' }}>
          Sign in to manage your SM-Compare listing
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {error && (
            <div style={{
              padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '8px', fontSize: '13px', color: '#991b1b', marginBottom: '16px',
            }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px', fontSize: '15px', fontWeight: '700',
              backgroundColor: loading ? '#93c5fd' : '#3b82f6', color: '#fff',
              border: 'none', borderRadius: '10px', cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ProviderLayout>
              <Routes>
                <Route path="/" element={<ProviderDashboard />} />
                <Route path="/leads" element={<LeadManagement />} />
                <Route path="/subscription" element={<SubscriptionManagement />} />
                <Route path="/featured" element={<FeaturedListingManagement />} />
                <Route path="/profile" element={<ProviderProfile />} />
                <Route path="/analytics" element={<ProviderAnalytics />} />
                <Route path="/reviews/basic" element={<ProviderReviews />} />
                <Route path="/api-access" element={<APIAccessPage />} />
                <Route path="/onboarding" element={<Navigate to="/onboarding/step/1" replace />} />
                <Route path="/onboarding/step/:step" element={<OnboardingWizard />} />
                <Route path="/reviews" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ReviewManagement /></React.Suspense>} />
                <Route path="/benchmark" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><CompetitorBenchmark /></React.Suspense>} />
                <Route path="/lead-settings" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><LeadSettings /></React.Suspense>} />
                <Route path="/translations" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderTranslations /></React.Suspense>} />
                <Route path="/engagement" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderEngagement /></React.Suspense>} />
                <Route path="/location" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderLocationManager /></React.Suspense>} />
                <Route path="/media" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MediaLibrary /></React.Suspense>} />
                <Route path="/setup-wizard" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderSetupWizard /></React.Suspense>} />
                <Route path="/favorite-stats" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderFavoriteStats /></React.Suspense>} />
                <Route path="/qa" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderQADashboard /></React.Suspense>} />
                <Route path="/api-keys" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderAPIKeys /></React.Suspense>} />
                <Route path="/promotions" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderPromotions /></React.Suspense>} />
                <Route path="/embed-widgets" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderEmbedWidgets /></React.Suspense>} />
                <Route path="/billing" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderBilling /></React.Suspense>} />
                <Route path="/deep-links" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderDeepLinks /></React.Suspense>} />
                <Route path="/alert-insights" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderAlertInsights /></React.Suspense>} />
                <Route path="/compliance-reports" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderComplianceReports /></React.Suspense>} />
                <Route path="/portfolio" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderPortfolioManager /></React.Suspense>} />
                <Route path="/trust-seals" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderTrustSeals /></React.Suspense>} />
                <Route path="/recommendation-insights" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderRecommendationInsights /></React.Suspense>} />
                <Route path="/feedback-insights" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderFeedbackInsights /></React.Suspense>} />
                <Route path="/affiliate-stats" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderAffiliateStats /></React.Suspense>} />
                <Route path="/subscription-tiers" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderSubscriptionPage /></React.Suspense>} />
                <Route path="/listing" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderListingEditor /></React.Suspense>} />
                <Route path="/badges" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderBadgesPage /></React.Suspense>} />
                <Route path="/pricing-benchmark" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderPricingBenchmark /></React.Suspense>} />
                <Route path="/service-areas" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyServiceAreas /></React.Suspense>} />
                <Route path="/wait-times" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyWaitTimes /></React.Suspense>} />
                <Route path="/events" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyEvents /></React.Suspense>} />
                <Route path="/review-incentives" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyReviewIncentives /></React.Suspense>} />
                <Route path="/communications" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderCommunications /></React.Suspense>} />
                <Route path="/response-templates" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ResponseTemplates /></React.Suspense>} />
                <Route path="/endorsements" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyEndorsements /></React.Suspense>} />
                <Route path="/hours" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManageHours /></React.Suspense>} />
                <Route path="/staff" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManageStaff /></React.Suspense>} />
                <Route path="/faq" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManageFAQ /></React.Suspense>} />
                <Route path="/price-history" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyPriceHistory /></React.Suspense>} />
                <Route path="/complaints" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderComplaints /></React.Suspense>} />
                <Route path="/disputes" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><DisputeCenter /></React.Suspense>} />
                <Route path="/analytics-overview" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><AnalyticsOverview /></React.Suspense>} />
                <Route path="/traffic" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><TrafficAnalytics /></React.Suspense>} />
                <Route path="/market-insights" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MarketInsights /></React.Suspense>} />
                <Route path="/performance-benchmark" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><PerformanceBenchmark /></React.Suspense>} />
                <Route path="/contact-requests" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ContactRequestsPage /></React.Suspense>} />
                <Route path="/support" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderSupportCenter /></React.Suspense>} />

                {/* Batch 30 Routes */}
                <Route path="/credentials" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManageCredentials /></React.Suspense>} />
                <Route path="/packages" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManagePackages /></React.Suspense>} />
                <Route path="/insurance" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManageInsurance /></React.Suspense>} />
                <Route path="/reminders" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderReminders /></React.Suspense>} />

                {/* Batch 31 Routes */}
                <Route path="/my-portfolio" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManagePortfolio /></React.Suspense>} />
                <Route path="/integrations" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyIntegrations /></React.Suspense>} />
                <Route path="/bulk-tools" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderBulkTools /></React.Suspense>} />
                <Route path="/exports" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderExports /></React.Suspense>} />
                <Route path="/availability" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ManageAvailability /></React.Suspense>} />

                {/* Batch 32 Routes */}
                <Route path="/compliance" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MyCompliance /></React.Suspense>} />
                <Route path="/competitor-insights" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><CompetitorInsights /></React.Suspense>} />
                <Route path="/settings" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderSettings /></React.Suspense>} />
                <Route path="/content" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderContent /></React.Suspense>} />
                <Route path="/audit-log" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderAuditLog /></React.Suspense>} />

                {/* Batch 33 Routes */}
                <Route path="/live-notifications" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><LiveNotifications /></React.Suspense>} />
                <Route path="/data-consent" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderDataConsent /></React.Suspense>} />
                <Route path="/sessions" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderSessions /></React.Suspense>} />

                {/* Batch 34 Routes */}
                <Route path="/api-versions" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderApiVersions /></React.Suspense>} />
                <Route path="/api-performance" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderApiPerformance /></React.Suspense>} />
                <Route path="/assets" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderAssets /></React.Suspense>} />
                <Route path="/service-health" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderServiceHealth /></React.Suspense>} />
                <Route path="/demo-data" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><ProviderDemoData /></React.Suspense>} />

                {/* Earnings Dashboard */}

                {/* Merchant Dashboard pages */}
                <Route path="/merchant-availability" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MerchantAvailability /></React.Suspense>} />
                <Route path="/merchant-promotions" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MerchantPromotions /></React.Suspense>} />
                <Route path="/merchant-settings" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><MerchantSettings /></React.Suspense>} />
                <Route path="/commissions" element={<React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}><CommissionsPage /></React.Suspense>} />
              </Routes>
            </ProviderLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
